import Icon from "$store/components/ui/Icon.tsx";
import PriceRange from "$store/islands/search/PriceRange.tsx";
import BrandsFilter from "$store/islands/search/BrandsFilter.tsx";
import type {
  Filter,
  FilterToggle,
  FilterToggleValue,
} from "apps/commerce/types.ts";

interface Props {
  filters: Filter[];
  clearFiltersUrl?: string;
}

const isToggle = (filter: Filter): filter is FilterToggle =>
  filter["@type"] === "FilterToggle";

export function ValueItem(
  { url, selected, label }: FilterToggleValue,
) {
  return (
    <a href={url} class="flex items-center gap-2" rel="nofollow">
      <div
        aria-checked={selected}
        class="checkbox checkbox-sm border-0 rounded bg-tw-base-100 flex justify-center items-center aria-checked:bg-none aria-checked:bg-tw-primary-100 group"
      >
        <Icon
          id="Check"
          class="text-white hidden group-aria-checked:block"
          width={12}
          height={9}
        />
      </div>

      <span class="text-tw-neutral-400 text-sm font-semibold">{label}</span>
    </a>
  );
}

function FilterValues({ key, values }: FilterToggle) {
  if (key === "price") {
    const range = values.reduce(
      (acc, curr) => {
        const [min, max] = curr.value.split(":").map(Number);

        return {
          min: Math.min(min, acc.min),
          max: Math.max(max, acc.max),
        };
      },
      { min: Infinity, max: 0 },
    );

    return <PriceRange {...range} />;
  }

  if (key === "brand") {
    return <BrandsFilter values={values} />;
  }

  return (
    <ul class="max-h-80 overflow-y-auto mt-3 grid gap-3 scrollbar">
      {values.map((item) => <ValueItem {...item} />)}
    </ul>
  );
}

function Filters({ filters, clearFiltersUrl }: Props) {
  return (
    <div class="w-full my-5">
      <span class="pb-4 border-b border-tw-neutral-100 text-tw-neutral-500 text-lg leading-none font-bold flex items-center gap-2 lg:pb-0 lg:border-0">
        Filtrar e Organizar
        <Icon id="Filter" class="text-tw-primary-100" width={17} height={12} />
      </span>

      <ul class="mt-3 grid gap-6">
        {filters
          .filter(isToggle)
          .sort((a, b) => {
            if (a.key === "price") {
              return 1;
            } else if (b.key === "price") {
              return -1;
            } else {
              return 0;
            }
          })
          .map((filter) => (
            <li>
              <details
                class={`group ${
                  filter.key !== "price"
                    ? "open:pb-6 open:border-b open:border-tw-primary-100 open:border-opacity-20"
                    : ""
                }`}
                open
              >
                <summary class="flex justify-between items-center cursor-pointer">
                  <span class="text-tw-neutral-500 text-lg font-bold">
                    {filter.label}
                  </span>

                  <Icon
                    id="ArrowBottom"
                    class="text-tw-primary-100 shrink-0 group-open:rotate-180"
                    width={12}
                    height={7}
                  />
                </summary>

                <FilterValues {...filter} />
              </details>
            </li>
          ))}
      </ul>

      {clearFiltersUrl && (
        <div class="pt-3 pb-8 bg-white sticky -bottom-5 left-0 right-0 z-10 lg:pt-5 lg:static">
          <a
            class="w-full h-12 rounded-lg bg-tw-primary-100 text-white font-maven font-extrabold flex justify-center items-center transition-colors duration-300 hover:bg-tw-primary-200"
            href={clearFiltersUrl}
            rel="nofollow"
          >
            Limpar filtros
          </a>
        </div>
      )}
    </div>
  );
}

export default Filters;
